import React from 'react';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import map from 'lodash/map';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { components } from 'react-select';
import Tooltip from '../mui/Tooltip';

export const SelectInput = (props) => {
  return (
    <components.Input
      data-testid={`input${props
        .getValue()
        ?.reduce((result, data) => result + '-' + data.value, '')}`}
      {...props}
      autoComplete="off"
    />
  );
};

export const SelectValue = ({ children, ...props }) => {
  return (
    <components.SingleValue
      innerProps={{
        'data-testid': `select-value`,
      }}
      {...props}
    >
      {children}
    </components.SingleValue>
  );
};

export const SelectOption = (props) => {
  const {
    data: { label, description, isDisabled, disabledMessage },
  } = props;
  const content = (
    <div className="select-option">
      <div data-testid={`select-option-${label}`} className="select-option-label">
        {label}
      </div>
      {description && <div className="select-option-description">{description}</div>}
    </div>
  );

  return (
    <components.Option {...props}>
      {isDisabled && disabledMessage ? (
        <Tooltip title={disabledMessage} arrow={true}>
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </components.Option>
  );
};

export const baseSelectStyles = {
  control: (styles) => ({
    ...styles,
    borderRadius: 8,
    fontSize: 14,
  }),
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      fontSize: 11,
      backgroundColor: isSelected ? '#2684FF' : isFocused ? '#B2D4FF' : '#fff',
      color: isSelected ? '#fff' : isDisabled ? ' #cccccc' : '#000',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    };
  },
  input: (styles) => ({
    ...styles,
    fontSize: 11,
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: 'white',
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: '4px 0',
  }),
  indicatorSeparator: (styles) => ({ ...styles, visibility: 'hidden' }),
  menu: (styles) => ({
    ...styles,
    fontSize: 11,
    marginTop: 0,
    fontWeight: 300,
    zIndex: 99999,
    whiteSpace: 'normal',
  }),
};

export const listSelectStyles = {
  ...baseSelectStyles,
  control: (styles) => ({
    ...styles,
    minHeight: 38,
    borderRadius: 8,
  }),
  container: (styles) => ({
    ...styles,
    width: 165,
    height: 38,
    fontSize: 12,
    marginRight: 8,
  }),
  option: (styles) => ({
    ...styles,
    padding: '0 12px',
    fontSize: 12,
    lineHeight: '32px',
  }),
};

export const darkSelectStyles = {
  control: (styles) => ({
    ...styles,
    minHeight: 32,
    fontSize: 11,
    backgroundColor: '#2c323e',
    borderColor: '#2c323e',
    fontWeight: 300,
  }),
  valueContainer: (styles) => ({
    ...styles,
    // fontSize: 11,
    color: '#fff',
  }),
  input: (styles) => ({
    ...styles,
    fontSize: 11,
    color: '#fff',
  }),
  singleValue: (styles) => ({
    ...styles,
    color: '#fff',
  }),
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      fontSize: 11,
      backgroundColor: isSelected || isFocused ? '#000000' : '#2c323e',
      color: isSelected ? '#31d134' : isDisabled ? ' #cccccc' : '#fff',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    };
  },
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: '4px 0',
  }),
  indicatorSeparator: (styles) => ({ ...styles, visibility: 'hidden' }),
  menu: (styles) => ({
    ...styles,
    fontSize: 11,
    marginTop: 0,
    borderColor: '#2c323e',
    backgroundColor: '#2c323e',
    fontWeight: 300,
    zIndex: 99999,
    whiteSpace: 'normal',
  }),
};

export const recipeBuilderSelectStyles = {
  ...baseSelectStyles,
  control: (styles) => ({
    ...styles,
    minHeight: 30,
    flexWrap: 'nowrap',
    background: '#fff',
    border: '1px solid #DFE8F0',
    cursor: 'pointer',
  }),
  input: (styles) => ({
    ...styles,
    margin: 0,
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: '0 12px',
    whiteSpace: 'nowrap',
  }),
  singleValue: (styles) => ({
    ...styles,
    margin: 0,
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: 5,
    color: '#454545',
  }),
  clearIndicator: (styles) => ({
    ...styles,
    padding: 5,
    color: '#454545',
  }),
  menu: (styles) => ({
    ...styles,
    boxShadow: '0 1px 2px 0px rgba(0, 0, 0, 0.1)',
    zIndex: 99999,
  }),
  option: (styles) => ({
    ...styles,
    padding: '5px 12px',
    fontSize: 12,
    lineHeight: '22px',
  }),
};

export const getCurrentSelectedOption = (
  selectionOptions,
  currentValue,
  defaultValue = null,
  valueKey = null,
  createNew = false,
) => {
  let effectiveValueKey = valueKey || 'value';
  let flatOptions = [];

  forEach(selectionOptions, (o) => {
    if (isArray(o.options)) {
      flatOptions.push(...o.options);
    } else {
      flatOptions.push(o);
    }
  });

  let currentSelectedOption = !isNil(currentValue)
    ? find(flatOptions, (o) => isEqual(o[effectiveValueKey], currentValue))
    : defaultValue;

  if (isUndefined(currentSelectedOption)) {
    currentSelectedOption =
      createNew && currentValue
        ? { label: currentValue, [effectiveValueKey]: currentValue }
        : defaultValue;
  }

  return currentSelectedOption;
};

export const getNewSelectionValue = (
  newSelection,
  getSimpleValue = false,
  defaultValue,
  valueKey = 'value',
) => {
  if (isArray(newSelection)) {
    return map(newSelection, (selection) =>
      getNewSelectionValue(selection, getSimpleValue, defaultValue, valueKey),
    );
  }

  if (getSimpleValue === true) {
    //handler expects a simple value instead of object
    let returnValue = defaultValue;
    if (newSelection && has(newSelection, valueKey)) {
      returnValue = newSelection[valueKey];
    }
    return returnValue;
  } else {
    return newSelection;
  }
};

export const redesignStyles = {
  container: (base) => ({ ...base, minWidth: 60, marginRight: 8 }),
  control: (base) => ({
    ...base,
    fontWeight: 500,
    lineHeight: '1.5rem',
    color: 'var(--color-greyscale-600)',
    fontSize: '14px',
    borderRadius: '8px',
    border: '1px solid var(--color-greyscale-100)',
    '&:hover': {
      borderColor: 'var(--color-greyscale-200)',
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '0  16px 0 12px' }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  indicatorContainer: (base) => ({ ...base, padding: 0 }),
  singleValue: (base) => ({
    ...base,
    fontWeight: 500,
    lineHeight: '1.5rem',
    color: 'var(--color-greyscale-600)',
    fontSize: '14px',
  }),
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      fontSize: '14px',
      backgroundColor: isSelected ? '#2684FF' : isFocused ? '#B2D4FF' : '#fff',
      color: isSelected ? '#fff' : isDisabled ? ' #cccccc' : 'var(--color-greyscale-600)',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    };
  },
  menu: (base) => ({
    ...base,
    zIndex: 99999,
    fontWeight: 500,
    lineHeight: '1.5rem',
    color: 'var(--color-greyscale-600)',
    fontSize: '14px',
  }),
};
